import { z } from 'zod'
import { mongoId, mongoIdSchema } from '../utils/basicSchemas/_id.z.schema'
import { timestampedDocSchema } from '../utils/basicSchemas/document.z.schema'

/**
 * Schema definition for a tagged user within a media content.
 * Contains details like user's profile image, username, positions on the media, and approval status.
 * 
 * @example
 * const taggedUser = taggedSchema.parse({
 *   userId: "123456",
 *   pos: {
 *     x: 50,
 *     y: 30
 *   }
 * });
 */
const taggedSchema = z.object({
    profileImage: z.string().optional(),
    username: z.string().optional(),
    userId: mongoIdSchema,
    groupIndex: z.number({ required_error: "Group index is required" }).default(0),
    mediaIndex: z.number({ required_error: "Media index is required" }).default(0),
    pos: z.object({
        x: z.number({ required_error: "X position is required" }),
        y: z.number({ required_error: "Y position is required" }),
    }),
    approved: z.boolean().default(false)
})

/**
 * Schema definition for tagging in a post.
 * Represents a tagging action within a post, which includes the post details and the tagged user.
 * 
 * @example
 * const taggingAction = taggingSchema.parse({
 *   post: "postId123",
 *   tagged: {
 *     userId: "123456",
 *     pos: {
 *       x: 50,
 *       y: 30
 *     }
 *   }
 * });
 */
const taggingSchema = z.object({
    post: mongoId("Tagging Post id"),
    object: z.string().nullable().optional(),
    notification: mongoIdSchema.optional(),
    tagged: taggedSchema,
})

/**
 * Zod schema representing a tagging document.
 * 
 * This schema extends {@link taggingSchema} by merging with {@link timestampedDocSchema} 
 * to include fields inherent to Mongoose documents like timestamps, `_id` and `__v`.
 * 
 * @example
 * const mytaggingSchema = taggingSchemaDoc.parse({
 *   _id: "603f650a0953de235c89eadc",
 *   __v: "1",
 *   post: "postId123",
 *   tagged: {
 *     userId: "123456",
 *     pos: {
 *       x: 50,
 *       y: 30
 *     }
 *   },
 *   createdAt: new Date(),
 *   updatedAt: new Date(),
 * });

 * 
 */
const taggingSchemaDoc = timestampedDocSchema.merge(taggingSchema)

// Type inference exports
type TaggedSchemaType = z.infer<typeof taggedSchema>
type TaggingSchemaType = z.infer<typeof taggingSchema>
type TaggingSchemaDocType = z.infer<typeof taggingSchemaDoc>

export { taggingSchemaDoc, taggingSchema }
export { TaggedSchemaType, TaggingSchemaType, TaggingSchemaDocType }
